<script>
import moment from "moment";
import axios from '@/utils/axios';
import "daterangepicker/daterangepicker.js";
import "daterangepicker/daterangepicker.css";
import DateRangePicker from "daterangepicker";
import Vue3ChartJs from "@j-t-mcc/vue3-chartjs";

export default {
  inject: ['setLoading'],
  components: { Vue3ChartJs },
  data() {
    return {
      errors: {},
      title: "Reports",
      text: "Inventory",
      text1: "Price Comparison",
      app_currency: '',
      filter: {
        from_date: '',
        to_date: '',
        item_id: '',
        vendor_id: '',
        price_filter: '',
      },
      summary: {},
      data: [],
      items: [],
      vendors: [],
      pagination: {
        current_page: 1,
        per_page: 25,
        last_page: 1,
        total: 0,
        links: []
      },
    };
  },

  computed: {
    serialNo() {
      return (this.pagination.current_page - 1) * this.pagination.per_page;
    },

    vendorBarChart() {
      if (!this.summary?.vendor_breakdown?.length) return null;
      const labels = this.summary.vendor_breakdown.map(v => v.vendor_name);
      const avgPrice = this.summary.vendor_breakdown.map(v => parseFloat(v.avg_price || 0));
      const avgTaxable = this.summary.vendor_breakdown.map(v => parseFloat(v.avg_taxable_unit || 0));
      return {
        type: 'bar',
        data: {
          labels,
          datasets: [
            {
              label: 'Avg Price',
              data: avgPrice,
              backgroundColor: '#4f46e5',
              borderRadius: 6,
              barThickness: 30,
            },
            {
              label: 'Avg Taxable Unit',
              data: avgTaxable,
              backgroundColor: '#06b6d4',
              borderRadius: 6,
              barThickness: 30,
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { display: true, position: 'top' } },
          scales: {
            y: { beginAtZero: true, grid: { color: '#f3f4f6' } },
            x: { grid: { display: false } }
          }
        }
      };
    },
  },

  async mounted() {
    this.app_currency = await this.$appCurrency();
    this.getDropdowns();

    const start = moment().subtract(29, "days");
    const end = moment();

    this.filter.from_date = start.format("YYYY-MM-DD");
    this.filter.to_date = end.format("YYYY-MM-DD");

    new DateRangePicker(
      this.$refs.dateRangeInput,
      {
        startDate: start,
        endDate: end,
        autoUpdateInput: false,
        locale: { format: "DD/MM/YYYY", separator: " - ", cancelLabel: "Clear" },
        ranges: {
          Today: [moment(), moment()],
          Yesterday: [moment().subtract(1, "days"), moment().subtract(1, "days")],
          "Last 7 Days": [moment().subtract(6, "days"), moment()],
          "Last 30 Days": [moment().subtract(29, "days"), moment()],
          "This Month": [moment().startOf("month"), moment().endOf("month")],
          "Last Month": [
            moment().subtract(1, "month").startOf("month"),
            moment().subtract(1, "month").endOf("month"),
          ],
        }
      },
      (start, end) => {
        this.filter.from_date = start.format("YYYY-MM-DD");
        this.filter.to_date = end.format("YYYY-MM-DD");
        this.$refs.dateRangeInput.value = start.format("DD/MM/YYYY") + " - " + end.format("DD/MM/YYYY");
        this.filterReport();
      }
    );
  },

  methods: {
    async filterReport(page = 1) {
      this.errors = {};

      if (!this.filter.item_id) {
        this.data = [];
        this.summary = {};
        return;
      }

      this.setLoading(true);
      try {
        const payload = { page };
        if (this.filter.from_date) payload.date_from = this.filter.from_date;
        if (this.filter.to_date) payload.date_to = this.filter.to_date;
        if (this.filter.item_id) payload.item_id = this.filter.item_id;
        if (this.filter.vendor_id) payload.vendor_id = this.filter.vendor_id;
        if (this.filter.price_filter) payload.price_filter = this.filter.price_filter;

        const query_string = new URLSearchParams(payload).toString();
        const response = await axios.get(`/reports/price-comparison?${query_string}`);
        this.summary = response.data.data.summary || {};
        this.data = response.data.data.rows.data;
        this.pagination = {
          current_page: response.data.data.rows.current_page,
          per_page: response.data.data.rows.per_page,
          last_page: response.data.data.rows.last_page,
          total: response.data.data.rows.total,
          links: response.data.data.rows.links
        };
      } catch (error) {
        if (error.type == 'validation') {
          this.errors = error.errors;
        } else {
          this.$swalToast(error.message, 'error');
        }
      } finally {
        this.setLoading(false);
      }
    },

    async getDropdowns() {
      try {
        const [itemRes, vendorRes] = await Promise.all([
          axios.get('/items/dropdown'),
          axios.get('/inventory/vendors/dropdown'),
        ]);
        this.items = itemRes.data.data;
        this.vendors = vendorRes.data.data;
      } catch (error) {
        console.log('Dropdown error:', error.message);
      }
    },

    changePage(url) {
      if (!url) return;
      const page = new URL(url).searchParams.get("page");
      this.filterReport(page);
    },

    hasActiveFilter() {
      return this.filter.item_id || this.filter.vendor_id || this.filter.from_date || this.filter.to_date || this.filter.price_filter;
    },

    clearFilter() {
      this.filter.item_id = '';
      this.filter.vendor_id = '';
      this.filter.from_date = '';
      this.filter.to_date = '';
      this.filter.price_filter = '';
      this.$refs.dateRangeInput.value = '';
      this.data = [];
      this.summary = {};
    },

    formatDate(dateStr) {
      if (!dateStr) return '-';
      return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    },

    formatCurrency(val) {
      return parseFloat(val || 0).toLocaleString('en-IN', { style: 'currency', currency: 'INR' });
    },

    referenceTypeLabel(type) {
      const map = {
        manual: 'Manual',
        purchase_order: 'Purchase Order',
        import: 'Import',
      };
      return map[type] || type;
    },

    referenceTypeClass(type) {
      const map = {
        manual: 'bg-secondary',
        purchase_order: 'bg-primary',
        import: 'bg-info',
      };
      return map[type] || 'bg-secondary';
    },

    async exportCSV() {
      if (!this.filter.item_id) return;

      this.setLoading(true);
      try {
        const payload = { export: 'csv' };
        if (this.filter.from_date) payload.date_from = this.filter.from_date;
        if (this.filter.to_date) payload.date_to = this.filter.to_date;
        if (this.filter.item_id) payload.item_id = this.filter.item_id;
        if (this.filter.vendor_id) payload.vendor_id = this.filter.vendor_id;
        if (this.filter.price_filter) payload.price_filter = this.filter.price_filter;

        const query_string = new URLSearchParams(payload).toString();
        const response = await axios.get(`/reports/price-comparison?${query_string}`, {
          responseType: 'blob',
        });

        const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `price-comparison-${this.filter.from_date}-to-${this.filter.to_date}.csv`;
        link.click();
        URL.revokeObjectURL(url);
      } catch (error) {
        this.$swalToast('Failed to export CSV', 'error');
      } finally {
        this.setLoading(false);
      }
    },
  }
};
</script>

<template>
  <layout-header></layout-header>
  <layout-sidebar></layout-sidebar>

  <div class="page-wrapper">
    <div class="content">

      <!-- Breadcrumb -->
      <div class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
        <breadcrumb :title="title" :text="text" :text1="text1" />
        <div class="d-flex my-xl-auto right-content align-items-center flex-wrap gap-2">
          <button class="btn btn-outline-primary d-flex align-items-center" @click="exportCSV"
            :disabled="!filter.item_id">
            <i class="ti ti-file-type-csv me-2"></i>Export CSV
          </button>
        </div>
      </div>

      <!-- Filters -->
      <div class="card border-0 mb-3">
        <div class="card-body d-flex align-items-center flex-wrap pb-3 gap-3">
          <div v-if="hasActiveFilter()">
            <a href="javascript:void(0);" class="text-danger" @click="clearFilter()">
              <i class="ti ti-filter-x"></i>
            </a>
          </div>
          <!-- Date Range -->
          <div>
            <div class="input-icon-end position-relative">
              <input type="text" class="form-control date-range bookingrange" ref="dateRangeInput"
                placeholder="dd/mm/yyyy - dd/mm/yyyy">
              <span class="input-icon-addon"><i class="ti ti-chevron-down"></i></span>
            </div>
          </div>
          <!-- Item Filter -->
          <div style="min-width: 150px;">
           <vue-select
              :options="items"
              :reduce="i => i.id"
              label="name"
              :model-value="filter.item_id || null"
              placeholder="Select Item *"
              @update:modelValue="filter.item_id = $event || ''; filterReport()"
              :clearable="true"
              />
          </div>
          <!-- Vendor Filter -->
          <div>
            <vue-select
              v-model="filter.vendor_id"
              :options="vendors"
              label="name"
              :reduce="v => v.id"
              placeholder="All Vendors"
              :clearable="true"
              @update:modelValue="filterReport()"
              style="min-width: 180px;"
            ></vue-select>
          </div>
          <!-- Price Filter -->
          <div>
            <vue-select
              v-model="filter.price_filter"
              :options="[
                { label: 'Low', value: 'low' },
                { label: 'High', value: 'high' },
              ]"
              label="label"
              :reduce="p => p.value"
              placeholder="Price Filter"
              :clearable="true"
              @update:modelValue="filterReport()"
              style="min-width: 180px;"
            ></vue-select>
          </div>
          <!-- Period Info -->
          <div v-if="summary.period_from" class="ms-auto">
            <span class="text-muted" style="font-size:11px;">
              <i class="ti ti-calendar me-1"></i>
              {{ formatDate(summary.period_from) }} — {{ formatDate(summary.period_to) }}
            </span>
          </div>
        </div>
      </div>

      <!-- Summary Cards -->
      <div class="row g-3 mb-3" v-if="summary && summary.total_records">
        <div class="col-xl col-md-3 col-6">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-body p-3 text-center">
              <span class="text-muted d-block" style="font-size:11px;">Total Records</span>
              <span class="fw-bold text-dark" style="font-size:20px;">{{ summary.total_records }}</span>
            </div>
          </div>
        </div>
        <div class="col-xl col-md-3 col-6">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-body p-3 text-center">
              <span class="text-muted d-block" style="font-size:11px;">Total Vendors</span>
              <span class="fw-bold text-dark" style="font-size:20px;">{{ summary.total_vendors }}</span>
            </div>
          </div>
        </div>
        <div class="col-xl col-md-3 col-6">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-body p-3 text-center">
              <span class="text-muted d-block" style="font-size:11px;">Total Qty</span>
              <span class="fw-bold text-dark" style="font-size:20px;">{{ parseFloat(summary.total_qty) }}</span>
            </div>
          </div>
        </div>
        <div class="col-xl col-md-3 col-6">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-body p-3 text-center">
              <span class="text-muted d-block" style="font-size:11px;">Min Price</span>
              <span class="fw-bold text-success" style="font-size:20px;">{{ formatCurrency(summary.min_price) }}</span>
            </div>
          </div>
        </div>
        <div class="col-xl col-md-3 col-6">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-body p-3 text-center">
              <span class="text-muted d-block" style="font-size:11px;">Max Price</span>
              <span class="fw-bold text-danger" style="font-size:20px;">{{ formatCurrency(summary.max_price) }}</span>
            </div>
          </div>
        </div>
        <div class="col-xl col-md-3 col-6">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-body p-3 text-center">
              <span class="text-muted d-block" style="font-size:11px;">Avg Price</span>
              <span class="fw-bold text-dark" style="font-size:20px;">{{ formatCurrency(summary.avg_price) }}</span>
            </div>
          </div>
        </div>
        <div class="col-xl col-md-3 col-6">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-body p-3 text-center">
              <span class="text-muted d-block" style="font-size:11px;">Avg Taxable Unit</span>
              <span class="fw-bold text-dark" style="font-size:20px;">{{ formatCurrency(summary.avg_taxable_unit)
                }}</span>
            </div>
          </div>
        </div>
        <div class="col-xl col-md-3 col-6">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-body p-3 text-center">
              <span class="text-muted d-block" style="font-size:11px;">Price Spread</span>
              <span class="fw-bold text-dark" style="font-size:20px;">{{ formatCurrency(summary.price_spread) }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Cheapest & Priciest Vendor -->
      <div class="row g-3 mb-3" v-if="summary && summary.total_records">
        <div class="col-md-6 col-6">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-body p-3 text-center">
              <span class="text-muted d-block" style="font-size:11px;">Cheapest Vendor</span>
              <span class="fw-bold text-dark" style="font-size:13px;">{{ summary.cheapest_vendor?.name || '-' }}</span>
              <span class="d-block text-success" style="font-size:11px;">{{
                formatCurrency(summary.cheapest_vendor?.price) }}</span>
            </div>
          </div>
        </div>
        <div class="col-md-6 col-6">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-body p-3 text-center">
              <span class="text-muted d-block" style="font-size:11px;">Priciest Vendor</span>
              <span class="fw-bold text-dark" style="font-size:13px;">{{ summary.priciest_vendor?.name || '-' }}</span>
              <span class="d-block text-danger" style="font-size:11px;">{{
                formatCurrency(summary.priciest_vendor?.price) }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Chart + Vendor Breakdown -->
      <div class="row g-3 mb-3" v-if="summary?.vendor_breakdown?.length">
        <!-- Bar Chart -->
        <div class="col-md-5">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-header border-0 pb-0">
              <h6 class="fw-bold mb-0"><i class="ti ti-chart-bar me-1 text-primary"></i>Vendor Price Comparison</h6>
            </div>
            <div class="card-body" style="height:280px;">
              <vue3-chart-js v-if="vendorBarChart" v-bind="vendorBarChart" :key="'bar-' + data.length" />
            </div>
          </div>
        </div>

        <!-- Vendor Breakdown Table -->
        <div class="col-md-7">
          <div class="card mb-0 border-0 shadow-sm h-100">
            <div class="card-header border-0 pb-0">
              <h6 class="fw-bold mb-0"><i class="ti ti-building-store me-1 text-primary"></i>Vendor Breakdown</h6>
            </div>
            <div class="card-body p-0">
              <div class="table-responsive">
                <table class="table table-sm mb-0">
                  <thead class="table-light">
                    <tr>
                      <th style="font-size:11px;">Vendor</th>
                      <th style="font-size:11px;">Records</th>
                      <th style="font-size:11px;">Total Qty</th>
                      <th style="font-size:11px;">Avg Price</th>
                      <th style="font-size:11px;">Price Range</th>
                      <th style="font-size:11px;">Avg Taxable</th>
                      <th style="font-size:11px;">Lowest Price Date</th>
                      <th style="font-size:11px;">Recent Price</th>
                      <th style="font-size:11px;">Last Seen</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="v in summary.vendor_breakdown" :key="v.vendor_id">
                      <td style="font-size:11px;">{{ v.vendor_name }}</td>
                      <td style="font-size:11px;">{{ v.record_count }}</td>
                      <td style="font-size:11px;">{{ parseFloat(v.total_qty) }}</td>
                      <td style="font-size:11px;" class="text-primary">{{ formatCurrency(v.avg_price) }}</td>
                      <td style="font-size:11px;">
                        {{ formatCurrency(v.min_price) }} – {{ formatCurrency(v.max_price) }}
                      </td>
                      <td style="font-size:11px;">{{ formatCurrency(v.avg_taxable_unit) }}</td>
                      <td style="font-size:11px;">{{ v.lowest_price_date || '-' }}</td>
                      <td style="font-size:11px;">
                        {{ formatCurrency(v.recent_price) }}
                        <br>
                        <span class="text-muted" style="font-size:10px;">{{ v.recent_price_date || '-' }}</span>
                      </td>
                      <td style="font-size:11px;">{{ formatDate(v.last_seen_at) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Main Table -->
      <div class="card border-0 shadow-sm">
        <div class="card-body card-table-wrapper p-0">
          <div class="custom-datatable-filter table-responsive">
            <table class="card-table" v-if="data && data.length">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Date</th>
                  <th>Reference</th>
                  <th>Type</th>
                  <th>Vendor</th>
                  <th>Item</th>
                  <th>Warehouse</th>
                  <th>Qty</th>
                  <th>Rate</th>
                  <th>Amount</th>
                  <th>Tax</th>
                  <th>Taxable Amt</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, index) in data" :key="row.id">
                  <td>{{ serialNo + index + 1 }}.</td>
                  <td style="white-space:nowrap;">{{ formatDate(row.created_at) }}</td>
                  <td>
                    <span class="fw-bold">{{ row.reference }}</span>
                  </td>
                  <td>
                    <span class="badge rounded-pill" :class="referenceTypeClass(row.reference_type)"
                      style="font-size:10px;">
                      {{ referenceTypeLabel(row.reference_type) }}
                    </span>
                  </td>
                  <td>{{ row.vendor_name }}</td>
                  <td>
                    <strong class="text-dark">{{ row.item_name }}</strong>
                    <br>
                    <span class="text-muted" style="font-size:11px;">{{ row.item_code }}</span>
                  </td>
                  <td>{{ row.warehouse_name || '-' }}</td>
                  <td>{{ parseFloat(row.qty) }}</td>
                  <td>
                    {{ formatCurrency(row.rate) }}
                    <i v-if="row.is_lowest_for_vendor" class="ti ti-arrow-down text-success ms-1"
                      style="font-size:12px;" title="Lowest price for this vendor"></i>
                  </td>
                  <td>{{ formatCurrency(row.amount) }}</td>
                  <td>
                    <span class="text-muted" style="font-size:11px;">{{ row.tax_name }}</span>
                    <br>
                    <span style="font-size:11px;">{{ formatCurrency(row.tax_amount) }}</span>
                  </td>
                  <td>{{ formatCurrency(row.taxable_amount) }}</td>
                </tr>
              </tbody>
            </table>

            <!-- Empty State -->
            <div v-else class="p-4 text-center text-muted">
              <template v-if="!filter.item_id">
                <i class="ti ti-select me-1"></i> Please select an item to view price comparison report.
              </template>
              <template v-else>
                No records found.
              </template>
            </div>

            <!-- Pagination -->
            <nav v-if="pagination.links && pagination.links.length > 3" class="m-3">
              <ul class="pagination pagination-sm">
                <li v-for="(link, index) in pagination.links" :key="index"
                  :class="['page-item', { active: link.active, disabled: !link.url }]">
                  <a class="page-link" href="javascript:void(0)" v-html="link.label" @click="changePage(link.url)"></a>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </div>

    </div>
    <app-footer></app-footer>
  </div>
</template>

<style scoped>
.table-responsive {
  overflow-y: auto;
}
</style>